import type { ReplicaDatabase } from '../db/replica-database';
import type { HttpClient } from '../http/http-client';
import type { MutationResult } from '../types/mutation-result';
import type { OutboxEntry } from '../types/outbox-entry';
import type { Rejection } from '../types/rejection';
import { storeRecords } from './store-records';

const BATCH = 200;
const MAX_ROUNDS = 20;

function rejectionOf(entry: OutboxEntry, result: MutationResult): Rejection {
  return {
    op_id: entry.op_id,
    collection: entry.collection,
    record_id: entry.record_id,
    kind: entry.kind,
    patch: entry.patch,
    code: result.code ?? 'sync.rejected',
    message: result.message ?? '',
    details: result.details ?? null,
    at: new Date().toISOString(),
  };
}

/** Push the outbox in order; returns what the server refused (already moved to `rejections`). */
export async function pushOutbox(
  db: ReplicaDatabase,
  http: HttpClient,
  deviceId: string,
): Promise<Rejection[]> {
  const refused: Rejection[] = [];
  for (let round = 0; round < MAX_ROUNDS; round++) {
    const batch = await db.outbox.orderBy('seq').limit(BATCH).toArray();
    if (batch.length === 0) break;
    const { results } = await http.json<{ results: MutationResult[] }>('/sync/push', {
      method: 'POST',
      body: JSON.stringify({
        device_id: deviceId,
        mutations: batch.map(({ op_id, collection, record_id, kind, patch, client_ts }) => ({
          op_id,
          collection,
          record_id,
          kind,
          patch,
          client_ts,
        })),
      }),
    });
    const byOp = new Map(batch.map((entry) => [entry.op_id, entry]));
    const confirmed = results.flatMap((r) => (r.record ? [r.record] : []));
    const rejected = results.flatMap((result) => {
      const entry = byOp.get(result.op_id);
      return entry && result.status === 'rejected' ? [rejectionOf(entry, result)] : [];
    });
    await db.transaction('rw', [db.records, db.outbox, db.rejections], async () => {
      await storeRecords(db, confirmed);
      await db.rejections.bulkPut(rejected);
      await db.outbox.bulkDelete(batch.flatMap((entry) => (entry.seq === undefined ? [] : [entry.seq])));
    });
    refused.push(...rejected);
  }
  return refused;
}
